import { AgGridCellTooltipComponent, AgGridCellTooltipValueGetter } from 'components/AgGrid/AgGridCellTooltipComponent';
import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import type { PriceListRatioRowData, RatioTypeEnum } from 'gql/graphql';
import { formatNumberToRu } from 'utils';

import type { TextFieldPropValue } from '@consta/uikit/TextField';
import type { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';

import type { TypeCheckedIds } from '../../WorkOrder/AddCoefficient';
import { CellRenderCheckbox } from '../../WorkOrder/AddCoefficient/cellRenderComponents/CellRenderCheckbox';
import { CellRenderTextField } from '../../WorkOrder/AddCoefficient/cellRenderComponents/CellRenderTextField';
import { getWorkOrderChangeRatioPriceList } from '../WorkOrderChangeQueries';

interface IColumnsAddCoefficient {
    data: PriceListRatioRowData[] | null;
    checkedIds: TypeCheckedIds;
    activeTabId: RatioTypeEnum;
    changeFilterHandler: (filterData: any) => void;
    handleChangesValueNote: (rowId: number) => ({ value }: { value: TextFieldPropValue }) => void;
    valueNote: Record<string, string>;
    handleChangeCheckbox: (row: PriceListRatioRowData) => ({ checked }: { checked: boolean }) => void;
    handleAllChangeCheckbox: (data: Record<string, boolean>) => () => void;
    workOrderChangePriceId: number;
    workOrderPriceId: number;
}

export const useColumnsWorkOrderChangeAddCoefficient = ({
    data,
    checkedIds,
    activeTabId,
    changeFilterHandler,
    handleChangesValueNote,
    valueNote,
    handleChangeCheckbox,
    handleAllChangeCheckbox,
    workOrderChangePriceId,
    workOrderPriceId,
}: IColumnsAddCoefficient) => {
    const isHeadCheckbox = true;

    const finalColumns = useMemo<ColDef[]>(
        () => [
            {
                headerName: '',
                field: 'checkbox',
                width: 46,
                cellClass: 'firstColumn',
                spanHeaderHeight: true,
                autoHeight: true,
                headerComponent: CellRenderCheckbox,
                headerComponentParams: {
                    checkedIds,
                    activeTabId,
                    handleChangeCheckbox,
                    handleAllChangeCheckbox,
                    currentRowData: data,
                    isHeadCheckbox,
                },
                cellRenderer: CellRenderCheckbox,
                cellRendererParams: {
                    checkedIds,
                    activeTabId,
                    handleChangeCheckbox,
                    handleAllChangeCheckbox,
                    currentRowData: data,
                },
            },
            {
                headerName: 'Шифр',
                field: 'code',
                autoHeight: true,
                width: 130,
                cellRenderer: ({ value }: { value: string }) => value || '',
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    dataAccessor: 'uerWorkOrderChangePriceListRatioQuery',
                    query: getWorkOrderChangeRatioPriceList,
                    variables: {
                        workOrderChangePriceId,
                        workOrderPriceId,
                    },
                    additionalWhereVariables: {
                        ratioType: {
                            eq: activeTabId,
                        },
                    },
                    withSearch: false,
                    childFieldAccessor: 'code',
                    columns: [
                        {
                            headerName: 'Шифр',
                            field: 'code',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                headerName: 'Наименование',
                field: 'workConditions',
                width: 410,
                tooltipComponent: AgGridCellTooltipComponent,
                tooltipValueGetter: AgGridCellTooltipValueGetter({}),
                cellRenderer: ({ value }: { value: string }) => <div>{value || ''}</div>,
                cellClass: 'ellipsis',
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    dataAccessor: 'uerWorkOrderChangePriceListRatioQuery',
                    query: getWorkOrderChangeRatioPriceList,
                    variables: {
                        workOrderChangePriceId,
                        workOrderPriceId,
                    },
                    additionalWhereVariables: {
                        ratioType: {
                            eq: activeTabId,
                        },
                    },
                    withSearch: false,
                    childFieldAccessor: 'workConditions',
                    columns: [
                        {
                            headerName: 'Наименование',
                            field: 'workConditions',
                            checkboxSelection: true,
                            width: 400,
                            lockVisible: true,
                            flex: 1,
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                headerName: 'К озп',
                field: 'kozp',
                autoHeight: true,
                align: 'center',
                width: 90,
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 2) || '',
            },
            {
                headerName: 'К эмм',
                field: 'kem',
                align: 'center',
                autoHeight: true,
                width: 90,
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 2) || '',
            },
            {
                headerName: 'К тз',
                field: 'ktz',
                autoHeight: true,
                align: 'center',
                width: 90,
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 2) || '',
            },
            {
                headerName: 'Ктз зпм',
                field: 'ktzzpm',
                autoHeight: true,
                align: 'center',
                width: 90,
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 2) || '',
            },
            {
                headerName: 'К мат',
                autoHeight: true,
                field: 'kmat',
                align: 'center',
                width: 90,
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 2) || '',
            },
            {
                headerName: 'Кнр',
                field: 'knr',
                autoHeight: true,
                align: 'center',
                width: 90,
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 2) || '',
            },
            {
                headerName: 'Ксп',
                field: 'ksp',
                align: 'center',
                autoHeight: true,
                width: 90,
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 2) || '',
            },
            {
                headerName: 'Примечание',
                field: 'note',
                autoHeight: true,
                width: 200,
                cellRenderer: CellRenderTextField,
                cellRendererParams: {
                    checkedIds,
                    activeTabId,
                    handleChangesValueNote,
                    valueNote,
                },
            },
        ],
        [
            activeTabId,
            changeFilterHandler,
            checkedIds,
            data,
            handleAllChangeCheckbox,
            handleChangeCheckbox,
            handleChangesValueNote,
            isHeadCheckbox,
            valueNote,
            workOrderChangePriceId,
            workOrderPriceId,
        ],
    );

    return {
        finalColumns,
    };
};
